import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslations } from '@lang';
import { Tabs, Tab } from '@common/components/Tabs';
import SectionContainer from '@common/components/SectionContainer';
import { SingleClass } from '@routes/SpaClasses/components/SingleClass';
import { Loader } from '@common/components/Spinner';
import { Markdown } from '@common/components';
import { StyledPageDescription } from '@common/features/EntitiesListing/EntitiesListings.styles';
import { Title1 } from '@common/components/Typography';

const NoClassesMessage = styled.div`
  display: flex;
  flex-direction: column;
  padding: 125px 0;

  h2 {
    font-family: ${({ theme }) => theme.headerFont};
    font-size: 35px;
    margin: 0;
  }
`;

const ClassesWrapper = styled.div`
  margin: -10px;
  margin-bottom: 40px;
  max-width: 100%;
`;

const DayPartTitle = styled.h2`
  font-size: 28px;
  font-family: ${({ theme }) => theme.headerFont};
  margin: 0 10px;
`;

const DATE_FORMAT = 'ddd D MMM';

const EmptyMessage = () => {
  const { t } = useTranslations();
  return (
    <NoClassesMessage>
      <h2>{t('spaClasses.noClasses')}</h2>
      {t('spaClasses.theAreNotClassesScheduled')}
    </NoClassesMessage>
  );
};
const ClassBoxWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spaces.space6};
`;

const SpinnerWrapper = ({ children, isLoading }) =>
  isLoading ? <Loader /> : <ClassBoxWrapper>{children}</ClassBoxWrapper>;

const ClassListing = ({
  isSpaClassesFetching,
  onTabChange,
  spaClasses = null,
  weekDays,
  selectedDate,
  handleBookClass,
  title,
  content,
  spaSettings = undefined,
}) => {
  const { t } = useTranslations();
  const spaClassesEntries = Object.entries(spaClasses ?? {});
  const getTabLabel = (t, date) =>
    moment(date).isSame(moment(), 'day') ? t('common.today') : moment(date).format(DATE_FORMAT);

  const getPreviousHour = (index) => {
    const [nextDate] = spaClassesEntries[index - 1] || [null];
    return nextDate ? moment(nextDate).hour() : null;
  };

  return (
    <Tabs
      selectedIndex={weekDays.findIndex((weekDay) => moment(weekDay).isSame(selectedDate, 'day'))}
    >
      {weekDays.map((date, index) => (
        <Tab key={index} label={getTabLabel(t, date)} value={date.format()} onClick={onTabChange}>
          <SectionContainer>
            <Title1 tag="h2">{title || t('spaClasses.classTimetable')}</Title1>
            {content && (
              <StyledPageDescription>
                <Markdown text={content} />
              </StyledPageDescription>
            )}
          </SectionContainer>
          <SectionContainer>
            <SpinnerWrapper isLoading={isSpaClassesFetching}>
              {spaClassesEntries.length > 0 ? (
                spaClassesEntries.map(([date, spaClasses], index) => (
                  <ClassesWrapper key={date}>
                    {index === 0 && moment(date).hour() < 12 && (
                      <DayPartTitle>{t('spaClass.morning')}</DayPartTitle>
                    )}
                    {moment(date).hour() >= 12 && (getPreviousHour(index) < 12 || index === 0) && (
                      <DayPartTitle>{t('spaClass.afternoon')}</DayPartTitle>
                    )}
                    {moment(date).hour() >= 18 && (getPreviousHour(index) < 18 || index === 0) && (
                      <DayPartTitle>{t('spaClass.evening')}</DayPartTitle>
                    )}
                    {spaClasses.map((spaClass) => (
                      <SingleClass
                        key={`${spaClass.classId}-${spaClass.startTime}`}
                        timeslot={spaClass}
                        onBookClass={handleBookClass}
                        spaSettings={spaSettings}
                      />
                    ))}
                  </ClassesWrapper>
                ))
              ) : (
                <EmptyMessage />
              )}
            </SpinnerWrapper>
          </SectionContainer>
        </Tab>
      ))}
    </Tabs>
  );
};

ClassListing.propTypes = {
  isSpaClassesFetching: PropTypes.bool.isRequired,
  selectedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  onTabChange: PropTypes.func.isRequired,
  weekDays: PropTypes.arrayOf(PropTypes.object).isRequired,
  spaClasses: PropTypes.arrayOf(PropTypes.shape({})),
  handleBookClass: PropTypes.func.isRequired,
  spaSettings: PropTypes.object,
};

export default ClassListing;
